/**
 * @lodex-pro/shared/validation/operator-portal-routes — operator portal rework PR 7 (0214):
 * the company's tank records (OPERATOR_ADMIN) and its routes (OPERATOR_ADMIN, FOREMAN).
 *
 * Every write is a definer in SQL that re-checks the company of each id (design §4.4); these
 * schemas only shape the requests.
 */

import { z } from 'zod';
import { upperCaseIdentifier } from './identifier.js';

const ids = z
  .array(z.string().uuid())
  .max(500)
  .transform((a) => [...new Set(a)]);

export const ConfirmTankProposalsRequest = z
  .object({
    haulerTankIds: ids.pipe(z.array(z.string()).min(1)),
    /**
     * #918 review MED-1 — hauler tanks whose key matches only a DEACTIVATED tank, for which the
     * admin chose a new tank (the alternative is reactivating the old one first). Without it,
     * such a tank refuses the confirm with OPERATOR_TANK_MATCHES_INACTIVE.
     */
    createNewFor: ids.optional(),
    /**
     * Tank registry PR 1 (0245): the name each NEW tank gets, by hauler tank id. Omitted = its
     * suggested name: the hauler's bare tank name (13.8, D-22c).
     */
    names: z.record(z.string().uuid(), z.string().trim().min(1).max(120)).optional(),
    /**
     * 13.8 review L3: hauler tanks whose ID or TAG match sits on another well, site or battery
     * (matchPlaceDiffers) and that the admin ticked "same tank" for. Without it, such a match
     * refuses the confirm with OPERATOR_TANK_MATCH_PLACE_DIFFERS. Each must be in haulerTankIds.
     */
    acceptPlaceDiffers: ids.default([]),
  })
  .strict()
  .refine((b) => b.acceptPlaceDiffers.every((id) => b.haulerTankIds.includes(id)), {
    message: 'every acceptPlaceDiffers id must be one of haulerTankIds',
    path: ['acceptPlaceDiffers'],
  });
/** What a client sends: acceptPlaceDiffers may be omitted (the server defaults it to []). */
export type ConfirmTankProposalsRequest = z.input<typeof ConfirmTankProposalsRequest>;

export const LinkTankRequest = z
  .object({ operatorTankId: z.string().uuid(), haulerTankId: z.string().uuid() })
  .strict();
export type LinkTankRequest = z.infer<typeof LinkTankRequest>;

export const UnlinkTankRequest = z.object({ haulerTankId: z.string().uuid() }).strict();
export type UnlinkTankRequest = z.infer<typeof UnlinkTankRequest>;

export const UpdateOperatorTankRequest = z
  .object({
    name: upperCaseIdentifier({ min: 1, max: 120 }),
    capacityBbl: z.number().positive().max(100000).nullable(),
    isActive: z.boolean(),
  })
  .strict();
export type UpdateOperatorTankRequest = z.infer<typeof UpdateOperatorTankRequest>;

export const SaveRouteRequest = z
  .object({ name: upperCaseIdentifier({ min: 1, max: 120 }), isActive: z.boolean().optional() })
  .strict();
export type SaveRouteRequest = z.infer<typeof SaveRouteRequest>;

export const SetRouteWellsRequest = z.object({ wellIds: ids }).strict();
export type SetRouteWellsRequest = z.infer<typeof SetRouteWellsRequest>;

export const SetRouteTanksRequest = z.object({ tankIds: ids }).strict();
export type SetRouteTanksRequest = z.infer<typeof SetRouteTanksRequest>;

export const RouteForemanRequest = z
  .object({ userId: z.string().uuid(), on: z.boolean() })
  .strict();
export type RouteForemanRequest = z.infer<typeof RouteForemanRequest>;

/** A hauler tank not yet linked, with the operator tank it would join (null = a new one). */
export interface TankProposal {
  haulerTankId: string;
  haulerTenantId: string;
  haulerName: string;
  tankName: string;
  capacityBbl: string | null;
  wellId: string | null;
  wellName: string | null;
  siteLabel: string | null;
  matchOperatorTankId: string | null;
  /** A DEACTIVATED tank with this key, when no active one has it: the admin decides (MED-1). */
  inactiveMatchOperatorTankId: string | null;
  /**
   * The name confirming gives a NEW tank (null when it joins an existing one): the hauler's bare
   * tank name (13.8, D-22c). Names are unique per well, site or battery; the admin may edit it.
   */
  suggestedName: string | null;
  /**
   * Tank registry PR 3 (0249), 13.8: how the match was found. ID = the hauler identifier equals
   * the tank's producer tank ID; TAG = it is one of the tank's tags; NAME = the same well or site
   * and name; null = no match.
   */
  matchReason: TankMatchReason | null;
  /** The hauler's own identifier for its tank (hauler_tanks.producer_identifier). */
  producerIdentifier: string | null;
  /** The hauler's bbl per inch, beside the matched tank's, to show the size difference. */
  bblPerInch: string | null;
  /** The matched (or inactive matched) operator tank: its name, sizes and place. */
  matchName: string | null;
  matchCapacityBbl: string | null;
  matchBblPerInch: string | null;
  matchPlaceLabel: string | null;
  /** An ID or TAG match whose tank is not at the hauler tank's well or site: warn (session 6). */
  matchPlaceDiffers: boolean;
  /**
   * 13.8 (D-20c): the producer tank ID a NEW tank would get (the hauler identifier, upper case),
   * or null: no identifier, it joins an existing tank, or the ID is taken.
   */
  suggestedProducerTankId: string | null;
  /** 13.8: the hauler identifier is already another active tank's producer ID; the new tank gets none. */
  producerIdTaken: boolean;
}

export const TANK_MATCH_REASONS = ['ID', 'TAG', 'NAME'] as const;
export type TankMatchReason = (typeof TANK_MATCH_REASONS)[number];

export interface OperatorTankLinkView {
  haulerTankId: string;
  haulerTenantId: string;
  haulerName: string;
  haulerTankName: string;
  linkedBy: 'MATCH' | 'MANUAL';
}

export interface OperatorTankView {
  id: string;
  name: string;
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  siteLabel: string | null;
  capacityBbl: string | null;
  isActive: boolean;
  links: OperatorTankLinkView[];
}

// ── Tank registry PR 1 (0245): the company's own tank records ─────────────────

const optionalNumber = (max: number): z.ZodOptional<z.ZodNullable<z.ZodNumber>> =>
  z.number().positive().max(max).nullable().optional();

/**
 * Create or edit one of the company's tanks. Its name is unique per placement among active tanks
 * (13.8, D-22); its producer tank ID, when given, across the company (D-20). It sits on exactly
 * one of a catalog well, a site label or one of the company's batteries. A catalog type fills the
 * size fields left empty.
 */
export const SaveRegistryTankRequest = z
  .object({
    name: upperCaseIdentifier({ min: 1, max: 120 }),
    /**
     * 13.8 (D-20): the producer's own ID for the tank, stored trimmed and UPPER CASE. Absent =
     * KEEP the stored one on an edit (none on a new tank); null or '' = clear it.
     */
    producerTankId: upperCaseIdentifier({ max: 120 })
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    wellId: z.string().uuid().nullable().optional(),
    siteLabel: upperCaseIdentifier({ max: 160 }).nullable().optional(),
    batteryId: z.string().uuid().nullable().optional(),
    capacityBbl: optionalNumber(100000),
    heightIn: optionalNumber(2000),
    bblPerInch: optionalNumber(1000),
    tankType: upperCaseIdentifier({ max: 60 }).nullable().optional(),
    standardTankCatalogId: z.string().uuid().nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict()
  .refine(
    (b) =>
      [
        b.wellId ?? null,
        b.siteLabel != null && b.siteLabel !== '' ? b.siteLabel : null,
        b.batteryId ?? null,
      ].filter((x) => x !== null).length === 1,
    { message: 'a tank sits on exactly one of a well, a site or a battery', path: ['wellId'] },
  );
export type SaveRegistryTankRequest = z.infer<typeof SaveRegistryTankRequest>;

export const TANK_TAG_SYSTEMS = ['SCADA', 'MQTT', 'OTHER'] as const;
export type TankTagSystem = (typeof TANK_TAG_SYSTEMS)[number];

/** A tag: compared as typed (an MQTT topic is case-sensitive), trimmed. */
export const AddTankTagRequest = z
  .object({ system: z.enum(TANK_TAG_SYSTEMS), tag: z.string().trim().min(1).max(200) })
  .strict();
export type AddTankTagRequest = z.infer<typeof AddTankTagRequest>;

export const SaveBatteryRequest = z
  .object({ name: upperCaseIdentifier({ min: 1, max: 120 }), isActive: z.boolean().optional() })
  .strict();
export type SaveBatteryRequest = z.infer<typeof SaveBatteryRequest>;

export const SetBatteryWellsRequest = z.object({ wellIds: ids }).strict();
export type SetBatteryWellsRequest = z.infer<typeof SetBatteryWellsRequest>;

export interface RegistryTankTagView {
  id: string;
  system: TankTagSystem;
  tag: string;
}

export interface RegistryTankLinkView extends OperatorTankLinkView {
  /** The hauler's own sizes, kept apart from the company's (Corbin: neither overwrites). */
  capacityBbl: string | null;
  bblPerInch: string | null;
}

export interface RegistryTankView {
  id: string;
  name: string;
  /** 13.8 (D-20): the producer tank ID, or null. */
  producerTankId: string | null;
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  siteLabel: string | null;
  batteryId: string | null;
  batteryName: string | null;
  capacityBbl: string | null;
  heightIn: string | null;
  bblPerInch: string | null;
  tankType: string | null;
  standardTankCatalogId: string | null;
  isActive: boolean;
  /** No bbl per inch while a reading given in inches waits for one to get its barrels. */
  needsSize: boolean;
  tags: RegistryTankTagView[];
  links: RegistryTankLinkView[];
}

export interface BatteryView {
  id: string;
  name: string;
  isActive: boolean;
  tankCount: number;
  wells: Array<{ wellId: string; wellName: string | null; apiNumber: string | null }>;
}

export interface CompanyWellView {
  wellId: string;
  apiNumber: string | null;
  wellName: string | null;
  wellType: string | null;
  /** SWD and INJ alike (Corbin: disposal, injection and SWD are the same thing). */
  isDisposal: boolean;
  state: string | null;
  county: string | null;
  tankCount: number;
  routeIds: string[];
}

export interface RouteView {
  id: string;
  name: string;
  isActive: boolean;
  wells: Array<{
    wellId: string;
    wellName: string | null;
    apiNumber: string | null;
    isDisposal: boolean;
  }>;
  tanks: Array<{ tankId: string; name: string; siteLabel: string | null }>;
  foremen: Array<{ userId: string; name: string | null }>;
  tankCount: number;
  updatedAt: string;
}

export interface CompanyForemanView {
  userId: string;
  name: string | null;
  email: string | null;
}

// ── Operator portal rework PR 8 (0215): tank selections (contacts) and coverage ──

/** Select or drop tanks (and every company tank on the given wells) for a person. */
export const SelectTanksRequest = z
  .object({
    /** Whom for; omitted = the caller (a pumper or foreman). */
    forUserId: z.string().uuid().optional(),
    tankIds: ids.default([]),
    wellIds: ids.default([]),
    selected: z.boolean(),
  })
  .strict()
  .refine((b) => b.tankIds.length + b.wellIds.length > 0, { message: 'nothing_to_select' });
export type SelectTanksRequest = z.infer<typeof SelectTanksRequest>;

export const SelectableTanksQuery = z.object({ forUserId: z.string().uuid().optional() }).strict();
export type SelectableTanksQuery = z.infer<typeof SelectableTanksQuery>;

/** A tank's contact: an ACTIVE member who selected it. */
export interface TankContactView {
  userId: string;
  name: string | null;
  role: string;
}

/** One of the company's tanks in the "My tanks" list, for one person. */
export interface SelectableTankView {
  tankId: string;
  name: string;
  siteLabel: string | null;
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  isDisposal: boolean;
  /** This person selected it (they are its contact). */
  selected: boolean;
  /** This person sees it through one of their routes without selecting it (a foreman). */
  viaRoute: boolean;
  contacts: TankContactView[];
  routeIds: string[];
}

/** Someone the caller may choose tanks for. */
export interface SelectionPersonView {
  userId: string;
  name: string | null;
  email: string | null;
  role: 'PUMPER' | 'FOREMAN';
  selectedCount: number;
}

/** A tank with no contact (the coverage flag). */
export interface UncoveredTankView {
  tankId: string;
  name: string;
  siteLabel: string | null;
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  routeIds: string[];
}

// ── Operator portal rework PR 10 (0217): requestor → pumper matches ──

export const REQUESTOR_LINK_STATUSES = ['LINKED', 'CONFLICT', 'DISMISSED'] as const;
export type RequestorLinkStatus = (typeof REQUESTOR_LINK_STATUSES)[number];

export const RequestorLinksQuery = z
  .object({ status: z.enum(REQUESTOR_LINK_STATUSES).optional() })
  .strict();
export type RequestorLinksQuery = z.infer<typeof RequestorLinksQuery>;

export const DecideRequestorLinkRequest = z
  .object({ decision: z.enum(['LINK', 'DISMISS']) })
  .strict();
export type DecideRequestorLinkRequest = z.infer<typeof DecideRequestorLinkRequest>;

/** One match of a company member to a hauler's requestor (or its job requester fields). */
export interface RequestorLinkView {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  haulerTenantId: string;
  haulerName: string;
  /** null = matched on the hauler's job requester fields only (no requestor record). */
  requestorId: string | null;
  requestorName: string | null;
  requestorEmail: string | null;
  requestorPhone: string | null;
  requestorCompany: string | null;
  matchKeyEmail: string | null;
  matchKeyPhone: string | null;
  matchedOn: 'EMAIL' | 'PHONE' | 'BOTH';
  status: RequestorLinkStatus;
  decidedByName: string | null;
  decidedAt: string | null;
  matchedAt: string;
  /** How many of the operator's jobs this requestor (or these fields) asked for. */
  jobCount: number;
}

/** Tanks on a well or battery where the caller requests loads, not yet selected (an offer). */
export interface RequestorWellOfferView {
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  siteLabel: string | null;
  tankIds: string[];
  tankNames: string[];
}

// ── Operator portal rework PR 11c (0221): the pumper's phone sends its queued changes ──

/**
 * One queued change from the phone. `occurredAt` is the phone's clock when the change was made,
 * `deviceSentAt` the phone's clock when it sent it (the server corrects the first by the
 * difference between the second and its own time). `tank_selection` is the only entity so far.
 */
export const OperatorSyncReceiveRequest = z
  .object({
    clientEventId: z.string().uuid(),
    entityType: z.literal('tank_selection'),
    occurredAt: z.string().datetime({ offset: true }),
    deviceSentAt: z.string().datetime({ offset: true }).optional(),
    payload: z
      .object({
        /** Whom for; omitted = the caller. */
        forUserId: z.string().uuid().optional(),
        tankIds: ids.default([]),
        wellIds: ids.default([]),
        selected: z.boolean(),
      })
      .strict()
      .refine((b) => b.tankIds.length + b.wellIds.length > 0, { message: 'nothing_to_select' }),
  })
  .strict();
export type OperatorSyncReceiveRequest = z.infer<typeof OperatorSyncReceiveRequest>;

/**
 * What came of it. `duplicate`: this clientEventId was received before; the counts are the first
 * time's. `superseded`: tanks left alone because they were changed later (on the web, or by a
 * later queued change). `skipped`: a pick of a tank that is no longer an active company tank (or is
 * not the company's); a drop also covers inactive company tanks, so only foreign ids skip.
 *
 * Refusals (#927 review): FINAL, drop the change and go on with the queue: 400 VALIDATION_FAILED,
 * 403 PERMISSION_DENIED / FEATURE_NOT_ENABLED, 422 OPERATOR_COMPANY_SCOPE_VIOLATION (the sender is
 * no longer an active pumper or foreman of the company). Retry later: 401, 408, 429 and 5xx.
 */
export interface OperatorSyncReceiveResponse {
  duplicate: boolean;
  changed: number;
  superseded: number;
  skipped: number;
}

// ── Operator portal rework PR 12a (0222): turning a load in ──

export const CreatePortalHaulRequest = z
  .object({
    operatorTankId: z.string().uuid(),
    haulerTenantId: z.string().uuid(),
    jobTypeId: z.number().int().positive(),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().trim().max(1000).optional(),
    /** Generated by the form (or the phone) per submit: a replay returns the first request. */
    clientRequestId: z.string().uuid(),
    /**
     * The pumper's phone marks its submits (Session 4, PR 12b); absent = the web portal. Only a
     * label on the job (requested_via): nothing is allowed or refused by it.
     */
    source: z.literal('PUMPER_MOBILE').optional(),
  })
  .strict();
export type CreatePortalHaulRequest = z.infer<typeof CreatePortalHaulRequest>;

/** One (tank, hauler) the caller can turn a load in for, with that hauler's job types. */
export interface HaulRequestTargetView {
  operatorTankId: string;
  tankName: string;
  siteLabel: string | null;
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  haulerTenantId: string;
  haulerName: string;
  /** false: this hauler cannot take it (EXPRESS for another customer). */
  available: boolean;
  unavailableReason: 'EXPRESS_OTHER_CUSTOMER' | null;
  jobTypes: Array<{ id: number; label: string }>;
}

/** A request as the portal shows it after submit, and in "My requests". */
export interface PortalHaulRequestView {
  jobId: string;
  haulId: string | null;
  haulerName: string;
  siteName: string | null;
  tankNames: string[];
  /** The Operator API's public status vocabulary (operator-api-job-status). */
  status: string;
  requestedVia: 'OPERATOR_API' | 'OPERATOR_PORTAL' | 'PUMPER_MOBILE' | null;
  /** The caller turned it in (else it is one of their matched requestor's loads). */
  mine: boolean;
  requestedAt: string;
  scheduledAt: string | null;
  completedAt: string | null;
}

// ── Tank registry PR 2: CSV / XLSX import (no migration) ─────────────────────

export type TankImportRowStatus = 'NEW' | 'UPDATE' | 'UNCHANGED' | 'ERROR';

export type TankImportRowError =
  | 'MISSING_NAME'
  | 'NAME_TOO_LONG'
  | 'MISSING_PLACEMENT'
  | 'MULTIPLE_PLACEMENTS'
  | 'UNKNOWN_WELL'
  | 'AMBIGUOUS_WELL'
  | 'UNKNOWN_BATTERY'
  | 'SITE_TOO_LONG'
  | 'INVALID_NUMBER'
  | 'UNKNOWN_CATALOG_TYPE'
  | 'TYPE_TOO_LONG'
  | 'INVALID_TAG'
  | 'TAG_TAKEN'
  | 'DUPLICATE_IN_FILE'
  | 'FORMULA_CELL'
  | 'AMBIGUOUS_NAME'
  | 'WRITE_FAILED'
  // 13.8 (D-22b): a name with no placement and no producer tank ID could be any of several tanks.
  | 'OPERATOR_TANK_IMPORT_NAME_NEEDS_PLACE'
  // 13.8 (D-20): the producer tank ID is already on another active tank.
  | 'OPERATOR_TANK_PRODUCER_ID_TAKEN'
  | 'PRODUCER_ID_TOO_LONG'
  | 'AMBIGUOUS_PRODUCER_ID';

/** One row of the file as the import reads it (preview), or as it was written (apply). */
export interface TankImportRow {
  rowNumber: number;
  /** As typed, trimmed. */
  name: string;
  /** 13.8: the producer tank ID the row gives (upper case), or null when its cell is blank. */
  producerTankId?: string | null;
  status: TankImportRowStatus;
  error?: TankImportRowError;
  /** A sentence for the screen. */
  detail?: string;
  /** The company's tank this row updates (UPDATE / UNCHANGED). */
  tankId?: string;
  /** "Well: Smith 1H (API …)", "Site: North yard", "Battery: East", or null when unknown. */
  placement: string | null;
  /** What an UPDATE changes: 'name', 'producer ID', 'placement', 'capacity', 'height', 'bbl/in', 'type', 'catalog type', 'tags', 'reactivate'. */
  changes: string[];
  /** Tags the row adds (the import never removes one). */
  addTags: Array<{ system: TankTagSystem; tag: string }>;
}

export interface TankImportSummary {
  total: number;
  new: number;
  update: number;
  unchanged: number;
  errors: number;
}

export interface TankImportResponse {
  applied: boolean;
  sheet: string;
  summary: TankImportSummary;
  rows: TankImportRow[];
  /** Apply only: what was written. */
  result?: { created: number; updated: number; tagsAdded: number };
}

/** A well of the company, for Paelode's tank editor (web-admin). */
export interface CompanyWellOption {
  wellId: string;
  apiNumber: string | null;
  wellName: string | null;
}
